// Account-managers data for the operator console. The INHERTA team and
// their client portfolios: a team KPI strip + a card per manager with load,
// availability and today's attention. Admin-only.
//
// Wired: getAccountManagers() -> GET /admin/account-managers,
// a cross-tenant aggregate. An account manager is any user who is a tenant's
// account_manager_id. Portfolio size + today's attention + derived load are
// real; fields with no Phase-1 source (level, role specialization, availability,
// avg response, coverage banner) default to the neutral baseline.

import { apiClient } from '../../api/client.js'

export type AmAvailability = 'active' | 'away'
export type AmLevel = 'Senior' | 'Gerente' | 'Junior'
export type AmLoadTone = 'warn' | 'ok' | 'mut'
export type BannerTone = 'warn' | 'ok' | 'away'

export interface AccountManager {
  id: string
  name: string
  initials: string
  level: AmLevel
  role: string
  email: string
  availability: AmAvailability
  availabilityLabel: string
  banner?: { tone: BannerTone; text: string }
  clients: number
  today: number | null
  avgResponse: string
  loadPct: number
  loadTone: AmLoadTone
  lastActive: string
}

export interface AmKpi {
  label: string
  value: string
  unit?: string
  sub: string
  subWarn?: boolean
}

export interface AccountManagersData {
  kpis: AmKpi[]
  managers: AccountManager[]
}

/**
 * GET /admin/account-managers — cross-tenant aggregate of the INHERTA team.
 * Portfolio size, today's attention and load are real; fields with no Phase-1
 * source (level, availability, avg response) come back as neutral baselines.
 */
export async function getAccountManagers(): Promise<AccountManagersData> {
  const res = await apiClient.get<{ data: AccountManagersData }>('/admin/account-managers')
  return res.data.data
}

/** The platform roles a INHERTA team member can hold. */
export const PLATFORM_ROLES = [
  { value: 'platform_account_manager', label: 'Gerente de cuenta' },
  { value: 'platform_operator', label: 'Operador' },
  { value: 'platform_admin', label: 'Administrador' },
  { value: 'platform_owner', label: 'Dueño de plataforma' },
] as const

/** Granting owner or admin is reserved to a platform_owner — the API enforces
 *  this too; this keeps the picker from offering a choice that would be refused. */
export function grantableRoles(
  inviterRole: string | undefined,
): readonly { value: string; label: string }[] {
  if (inviterRole === 'platform_owner') return PLATFORM_ROLES
  return PLATFORM_ROLES.filter((r) => r.value !== 'platform_owner' && r.value !== 'platform_admin')
}

/** Invite someone to the INHERTA team. `deliveryEmail`, when given, sends the
 *  invite mail to a different inbox than the account being created — the
 *  accept flow still binds the account to `email`. */
export async function inviteTeamMember(
  email: string,
  platformRole: string,
  deliveryEmail?: string,
): Promise<{ email_sent: boolean }> {
  const res = await apiClient.post<{ data: { ok: boolean; email_sent?: boolean } }>(
    '/admin/users/invite',
    {
      email,
      platform_role: platformRole,
      ...(deliveryEmail ? { delivery_email: deliveryEmail } : {}),
    },
  )
  return { email_sent: res.data.data.email_sent ?? true }
}
